import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  acceptTesterInviteErrorKey,
  useAcceptTesterInviteByToken,
} from "@/hooks/use-my-tester-invites";
import {
  testerInviteLookupErrorKey,
  useTesterInviteLookup,
  type TesterInviteLookupResult,
} from "@/hooks/use-tester-invite-lookup";
import { testerInviteCtaTargets } from "@/lib/tester-invite-links";
import { isRateLimited } from "@/lib/rate-limited";
import { useCompanyStore } from "@/store";
import { Button, Card, CardContent } from "@/components/ui";

interface AcceptTesterInviteSearch {
  token?: string;
}

export const Route = createFileRoute("/accept-tester-invite")({
  // The token is a 32-byte hex credential. A value of any other shape never
  // reaches the wire: it cannot be a real token, so posting it would only
  // spend a rate-limit slot to be told so.
  validateSearch: (search: Record<string, unknown>): AcceptTesterInviteSearch => ({
    token:
      typeof search.token === "string" && /^[a-f0-9]{64}$/.test(search.token)
        ? search.token
        : undefined,
  }),
  component: AcceptTesterInviteLinkPage,
});

const CTA_BASE =
  "inline-flex w-full items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors";
const CTA_PRIMARY = `${CTA_BASE} bg-primary text-on-primary hover:bg-primary-hover`;
const CTA_SECONDARY = `${CTA_BASE} border border-gray-300 text-muted hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700`;

function InviteCard({ children }: { children: ReactNode }) {
  const { t } = useTranslation("company");
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <Card>
        <CardContent>
          <div className="max-w-md space-y-4 p-6 text-center">
            <h1 className="text-2xl font-bold text-on-surface">{t("testerInvite.title")}</h1>
            {children}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Waiting({ label }: { label: string }) {
  return (
    <>
      <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-sm text-muted">{label}</p>
    </>
  );
}

/**
 * `onRetry` is offered ONLY where waiting can change the answer — the
 * rate-limit refusal. Every other failure this card renders is a verdict on
 * the invite itself, and a retry button there would loop a visitor through
 * the same sentence forever.
 */
function Refusal({
  message,
  onExit,
  onRetry,
}: {
  message: string;
  onExit: () => void;
  onRetry?: () => void;
}) {
  const { t } = useTranslation("company");
  return (
    <>
      <p className="text-sm text-danger">{message}</p>
      {onRetry ? <Button onClick={onRetry}>{t("testerInvite.retryCta")}</Button> : null}
      <Button variant={onRetry ? "secondary" : "primary"} onClick={onExit}>
        {t("testerInvite.continueToPortal")}
      </Button>
    </>
  );
}

/**
 * What an invitee with no session sees: who invited them, at which address,
 * and the two ways onto the account that can spend the token.
 *
 * `account_exists` decides which exit LEADS, never which exits exist. It is a
 * probe of the invited address, so a visitor whose account sits under a
 * different one would otherwise be trapped on a screen offering only the wrong
 * door.
 */
function PreAuthInvite({ token, invite }: { token: string; invite: TesterInviteLookupResult }) {
  const { t } = useTranslation(["company", "auth"]);
  const { signIn, signUp } = testerInviteCtaTargets(token);
  const exists = invite.account_exists;

  const signInLink = (
    <Link to={signIn.to} search={signIn.search} className={exists ? CTA_PRIMARY : CTA_SECONDARY}>
      {t("auth:signInLink")}
    </Link>
  );
  const createAccountLink = (
    <Link to={signUp.to} search={signUp.search} className={exists ? CTA_SECONDARY : CTA_PRIMARY}>
      {t("auth:createAccount")}
    </Link>
  );

  return (
    <>
      <p className="text-sm text-on-surface">
        {t("testerInviteLookup.body", { orgName: invite.org_name, email: invite.email })}
      </p>
      <p className="text-sm text-muted">
        {exists
          ? t("testerInviteLookup.signInPrompt")
          : t("testerInviteLookup.createAccountPrompt")}
      </p>
      <div className="flex flex-col gap-2">
        {exists ? signInLink : createAccountLink}
        {exists ? createAccountLink : signInLink}
      </div>
    </>
  );
}

/**
 * The email-link landing page for a tester invite. Accepting mints the
 * `org_testers` authorization with status `active` — the utility-initiated
 * invite IS the verification act, and the token is what proves the visitor
 * controls the invited mailbox.
 *
 * The route is PUBLIC. An invitee who has no account yet must be able to read
 * WHO invited them before deciding to create one, so an anonymous arrival
 * resolves the token through the pre-auth lookup and is offered sign-in and
 * sign-up; only an arrival that already holds a session spends the token.
 *
 * It sits OUTSIDE the `_company` shell on purpose: an invitee is neither a
 * company member nor yet a tester, so the persona gate would terminal-screen
 * them before the token could be spent. Landing on "/" afterwards is what
 * hands them back to that gate, which now resolves the tester persona.
 */
function AcceptTesterInviteLinkPage() {
  const { t } = useTranslation("company");
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const isAuthenticated = useCompanyStore((s) => s.isAuthenticated);
  const acceptMutation = useAcceptTesterInviteByToken();
  const attemptedRef = useRef(false);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  // Passing `undefined` is what holds the lookup disabled — the hook gates on
  // the token's presence. A session-holding arrival spends the token instead
  // of resolving it, and must issue no lookup at all.
  const lookup = useTesterInviteLookup(isAuthenticated ? undefined : token);

  useEffect(() => {
    if (!token || !isAuthenticated || attemptedRef.current) return;
    attemptedRef.current = true;
    // The outcome is awaited off the mutation's OWN promise and held here,
    // rather than read off `isPending` / `isError`. This accept is driven by
    // ARRIVAL, not by a click, so it starts inside the mount effect — and
    // React's development remount tears the mutation observer's subscription
    // down mid-flight, after which it is never told the request settled and
    // `mutate`'s callbacks never run. The page would sit on its spinner
    // forever. The promise has no such bookkeeping.
    acceptMutation.mutateAsync(token).then(
      (result) => {
        toast.success(t("testerInvite.acceptedFor", { orgName: result.org_name }));
        void navigate({ to: "/" });
      },
      (error: unknown) => {
        setFailure({ error });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once-only is the ref's job, not the deps': the omitted mutation/navigate/t identities change every render, and the ref latches only after the guard passes so a session arriving later in the same mount still accepts
  }, [token, isAuthenticated]);

  const exit = () => void navigate({ to: "/" });
  // A reload, not a refetch: the accept runs from the mount effect behind a
  // once-only ref, so a fresh mount is the only thing that can spend the token
  // again — and the same mount re-resolves the lookup for the anonymous arm.
  const retry = () => window.location.reload();

  if (!token) {
    return (
      <InviteCard>
        <Refusal message={t("testerInvite.errorInvalidToken")} onExit={exit} />
      </InviteCard>
    );
  }

  if (!isAuthenticated) {
    if (lookup.isError) {
      return (
        <InviteCard>
          <Refusal
            message={t(testerInviteLookupErrorKey(lookup.error))}
            onExit={exit}
            onRetry={isRateLimited(lookup.error) ? retry : undefined}
          />
        </InviteCard>
      );
    }
    if (lookup.data === undefined) {
      return (
        <InviteCard>
          <Waiting label={t("testerInviteLookup.loading")} />
        </InviteCard>
      );
    }
    return (
      <InviteCard>
        <PreAuthInvite token={token} invite={lookup.data} />
      </InviteCard>
    );
  }

  // Wrapped, so a refusal that carries a falsy error still reads as a refusal.
  return (
    <InviteCard>
      {failure ? (
        <Refusal
          message={t(acceptTesterInviteErrorKey(failure.error))}
          onExit={exit}
          onRetry={isRateLimited(failure.error) ? retry : undefined}
        />
      ) : (
        <Waiting label={t("testerInvite.accepting")} />
      )}
    </InviteCard>
  );
}
